/* eslint-disable no-underscore-dangle, react-hooks/exhaustive-deps */
/* Removing the underscore gives a weird getter error */
import React, { useState, useEffect, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import jsCookie from 'js-cookie';

import { ThemeProvider, ashTheme } from '@glu/theming';

import Splash from 'components/splash/Splash';

import withRouterConnect from '../withRouterConnect/withRouterConnect';
import { AppConfigContext } from '../appConfig/AppConfig';
import { fetchTheme, fetchUserTheme } from '../../api/theme/service';
import themeOverrides from './themeOverrides.ts';

const CustomThemeProvider = ({ authError, children, service, userThemeService }) => {
  const [theme, setTheme] = useState({});
  const { isNative } = useContext(AppConfigContext);
  const getUserTheme = () => userThemeService().then((userTheme) => setTheme(userTheme));
  const memoizedAPICall = useMemo(() => service(), []);
  const getDefaultTheme = () => memoizedAPICall.then((defaultTheme) => setTheme(defaultTheme));
  const authenticationId = useSelector((state) => state.auth?.user?.authenticationId);

  useEffect(() => {
    const jwtToken = jsCookie.get('jwtToken');
    const sessionStorageJWTToken = sessionStorage.getItem('jwtToken');

    // If user has token but does not have redux details yet
    // Or if user has no tokens
    if (authError || (!jwtToken && !sessionStorageJWTToken)) {
      getDefaultTheme();
    }
    // If user is logged in then load user theme
    if (authenticationId) {
      setTheme({});
      getUserTheme();
    }
  }, [authenticationId, authError]);

  if (Object.keys(theme).length === 0) return <Splash stage="Loading Theme..." />;

  const fullTheme = {
    ...theme,
    updateTheme: getUserTheme,
    ...(isNative && {
      palette: {
        ...theme.palette,
        background: theme.palette?.background?.normal || theme.palette?.background
      }
    }),
    ...themeOverrides(ashTheme())
  };

  return (
    <ThemeProvider baseTheme={ashTheme} theme={fullTheme}>
      {typeof children === 'function' ? children({ theme, ready: true }) : children}
    </ThemeProvider>
  );
};

CustomThemeProvider.propTypes = {
  authError: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.func]).isRequired,
  service: PropTypes.func,
  userThemeService: PropTypes.func
};

CustomThemeProvider.defaultProps = {
  authError: null,
  service: fetchTheme,
  userThemeService: fetchUserTheme
};

/* istanbul ignore next */
const mapStateToProps = (state) => ({
  authError: state?.auth?.error?.code
});

export default withRouterConnect(mapStateToProps, {})(CustomThemeProvider);
